import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { 
  Sparkles, Layers, Box, Zap, 
  ArrowRight, Play, Star, MousePointer2
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import Process from "../components/Process";
import SEO from "../components/SEO";
import ParticleGame from "../components/ParticleGame";
import SectionStepper from "../components/SectionStepper";

const motionSteps = [
  {
    step: "01",
    title: "Styleframes",
    desc: "Designing the look and feel of the animation through static high-fidelity frames.",
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "02",
    title: "Motion Sketches",
    desc: "Creating rough animations to establish timing, rhythm, and kinetic energy.",
    image: "https://images.unsplash.com/photo-1616469829581-73993eb86b02?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "03",
    title: "Refinement",
    desc: "Smoothing curves, adding secondary motion, and polishing every single keyframe.",
    image: "https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=1200"
  },
  {
    step: "04",
    title: "VFX & Compositing",
    desc: "Adding final effects, lighting, and integrating elements into a cohesive final render.",
    image: "https://images.unsplash.com/photo-1628494391268-c9935bc384d5?auto=format&fit=crop&q=80&w=1200"
  }
];

const MotionGraphics = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  const stepperSections = [
    { id: "hero", label: "Motion Art" },
    { id: "capabilities", label: "Storytelling" },
    { id: "process", label: "Workflow" },
    { id: "game", label: "Kinetic Lab" }
  ];

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO 
        title="Premium Motion Graphics" 
        description="Kinetic typography, 2D/3D animations, and cinematic title sequences that move hearts and eyeballs."
      />
      {/* <CustomCursor /> */}
      <SectionStepper sections={stepperSections} />
      <div className="grain" />
      <Navbar />

      <main>
        {/* Hero */}
        <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <motion.div
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.55 }}
              transition={{ duration: 1.5 }}
              className="w-full h-full"
            >
              <video 
                autoPlay 
                muted 
                loop 
                playsInline 
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-75"
              >
                <source src="https://videos.pexels.com/video-files/34934668/14798533_1920_1080_30fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </div>

          <div className="container-xl w-full relative z-20 px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-5xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
                Kinetic Artistry
              </span>
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mb-8 leading-tight">
                Design in <br />
                <span className="italic font-serif-accent gold-text">Motion</span>
              </h1>
              <p className="max-w-2xl text-[var(--cream-muted)] text-lg md:text-xl mb-12 font-body leading-relaxed">
                We breathe life into static design. From minimalist kinetic typography to complex 3D environments, we create visuals that capture attention and never let go.
              </p>
              <div className="flex flex-col md:flex-row items-start justify-start gap-6">
                <Link to="/contact" className="btn-primary">
                  Animate Your Brand <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <a href="#game" className="btn-ghost">
                  Enter Workshop
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section id="capabilities" className="py-32 relative">
          <div className="container-xl px-6">
            <Kicker index="01" label="Capabilities" />
            <h2 className="mt-8 font-display text-4xl md:text-6xl text-[var(--cream)] mb-20">
              Visual <span className="italic gold-text">Storytelling</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: <Sparkles />, title: "2D Animation", desc: "Clean, kinetic motion that simplifies complex ideas." },
                { icon: <Box />, title: "3D Environments", desc: "Immersive 3D worlds and product visualisations." },
                { icon: <Layers />, title: "Title Sequences", desc: "Cinematic openers that set the tone for your film." }
              ].map((s, i) => (
                <div key={i} className="p-10 bg-[var(--ink-2)] border border-[var(--rule)] rounded-2xl hover:border-[var(--gold)] transition-all duration-500 group">
                  <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform origin-left">{s.icon}</div>
                  <h3 className="font-display text-2xl mb-4">{s.title}</h3>
                  <p className="text-[var(--muted)] text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Process id="process" steps={motionSteps} title="The Motion Workflow" kicker={{ index: "02", label: "The Flow" }} />

        <ParticleGame id="game" />
      </main>

      <Footer />
    </div>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default MotionGraphics;
